import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, ClipboardList, TrendingUp, ThumbsDown, Send, BarChart2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORES_ZONA = [
  "#b91c1c", "#1d4ed8", "#15803d", "#b45309", "#7c3aed",
  "#0e7490", "#be185d", "#4d7c0f", "#92400e", "#374151",
];

const COLORES_PIE = {
  "Con compra ERP":   "#15803d",
  "Sin registros ERP":"#94a3b8",
  "Inactivas":        "#e2e8f0",
  "Enviadas":         "#1d4ed8",
  "Rechazadas":       "#b91c1c",
  "Pendientes":       "#b45309",
};

function KpiCard({ icon: Icon, label, value, sub, color }) {
  const cls = {
    red:   "bg-red-50   border-red-100   text-red-700",
    blue:  "bg-blue-50  border-blue-100  text-blue-700",
    green: "bg-green-50 border-green-100 text-green-700",
    slate: "bg-slate-50 border-slate-100 text-slate-700",
    amber: "bg-amber-50 border-amber-100 text-amber-700",
  }[color] || "bg-slate-50 border-slate-100 text-slate-700";

  return (
    <Card className={`border ${cls} shadow-none`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide opacity-60">{label}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
          </div>
          <Icon className="w-9 h-9 opacity-15 mt-1" />
        </div>
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-medium text-slate-700">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700">{name}</p>
      <p className="text-slate-500">{value} notificaciones</p>
    </div>
  );
}

export default function Reportes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/reportes/resumen`);
      setData(d);
    } catch {
      toast.error("Error al cargar reportes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Cargando reportes…
      </div>
    );
  }

  if (!data) return null;

  const { kpis, por_zona, top_medicamentos, compras_estado, notif_estado, clientes_con_rechazos } = data;

  // Limitar zonas al top 10 para el gráfico
  const zonaData = por_zona.slice(0, 10).map(z => ({
    ...z,
    name: z.zona.length > 14 ? z.zona.slice(0, 12) + "…" : z.zona,
    fullName: z.zona,
  }));

  const medData = top_medicamentos.slice(0, 10).map(m => ({
    ...m,
    name: m.medicamento.length > 22 ? m.medicamento.slice(0, 20) + "…" : m.medicamento,
    fullName: m.medicamento,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-red-700" /> Reportes
          </h1>
          <p className="text-sm text-slate-400">Resumen general del programa Ahorra con Farmavida</p>
        </div>
        <Button variant="outline" onClick={load} className="border-slate-200 text-slate-500 text-xs h-8">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Actualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users}        label="Clientes activos"      value={kpis.clientes_activos}         sub={`de ${kpis.total_clientes} registrados`}          color="blue"  />
        <KpiCard icon={ClipboardList} label="Inscripciones activas" value={kpis.inscripciones_activas}    sub={`de ${kpis.total_inscripciones} total`}            color="slate" />
        <KpiCard icon={TrendingUp}   label="Con compra en ERP"     value={`${kpis.tasa_compra_erp}%`}    sub="de inscripciones activas"                          color="green" />
        <KpiCard icon={ThumbsDown}   label="Tasa de rechazo"       value={`${kpis.tasa_rechazo}%`}       sub={`${kpis.total_notif_rechazadas} rechazadas`}       color="red"   />
      </div>

      {/* Fila 1: Clientes por zona + Notificaciones */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Clientes activos por zona */}
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Clientes activos por zona</CardTitle>
          </CardHeader>
          <CardContent>
            {zonaData.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-8">Sin datos de zonas</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={zonaData} margin={{ top: 4, right: 10, left: -20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="clientes_activos" name="Clientes activos" radius={[4,4,0,0]}>
                    {zonaData.map((_, i) => (
                      <Cell key={i} fill={COLORES_ZONA[i % COLORES_ZONA.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Notificaciones pie */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Estado de notificaciones</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={notif_estado} dataKey="cantidad" nameKey="estado" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {notif_estado.map((entry, i) => (
                    <Cell key={i} fill={COLORES_PIE[entry.estado] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 w-full mt-1">
              {notif_estado.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORES_PIE[e.estado] || "#94a3b8" }} />
                    <span className="text-slate-600">{e.estado}</span>
                  </div>
                  <span className="font-semibold text-slate-700">{e.cantidad}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fila 2: Top medicamentos + Estado compras */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Top medicamentos */}
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Top 10 medicamentos más inscritos</CardTitle>
          </CardHeader>
          <CardContent>
            {medData.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-8">Sin datos</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={medData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" name="Inscripciones" fill="#b91c1c" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Estado inscripciones pie */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Historial de compras ERP</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={compras_estado} dataKey="cantidad" nameKey="estado" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {compras_estado.map((entry, i) => (
                    <Cell key={i} fill={COLORES_PIE[entry.estado] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 w-full mt-1">
              {compras_estado.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLORES_PIE[e.estado] || "#94a3b8" }} />
                    <span className="text-slate-600">{e.estado}</span>
                  </div>
                  <span className="font-semibold text-slate-700">{e.cantidad}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fila 3: Inscripciones por zona (stacked) */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">Inscripciones activas vs inactivas por zona</CardTitle>
        </CardHeader>
        <CardContent>
          {zonaData.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-8">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={zonaData} margin={{ top: 4, right: 10, left: -20, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Bar dataKey="inscripciones_activas"   name="Activas"   stackId="a" fill="#15803d" radius={[0,0,0,0]} />
                <Bar dataKey="inscripciones_inactivas" name="Inactivas" stackId="a" fill="#e2e8f0" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Fila 4: Clientes con rechazos */}
      {clientes_con_rechazos.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <ThumbsDown className="w-4 h-4 text-orange-500" />
              Clientes con rechazos registrados
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Cliente</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Zona</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Teléfono</th>
                  <th className="text-center px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Rechazos</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Reactivación</th>
                </tr>
              </thead>
              <tbody>
                {clientes_con_rechazos.map((c, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-2.5 font-medium text-slate-800">{c.nombre}</td>
                    <td className="px-5 py-2.5 text-slate-500">{c.zona}</td>
                    <td className="px-5 py-2.5 text-slate-500">{c.telefono}</td>
                    <td className="px-5 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                        c.rechazos >= 2 ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"
                      }`}>
                        {c.rechazos}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-slate-500 text-xs">
                      {c.fecha_reactivacion
                        ? c.fecha_reactivacion.split("-").reverse().join("/")
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
